import React, { useContext } from "react";
import { ArrowRight, CheckCircle2, Crown, ShieldCheck, Sparkles, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import { Button, MetricCard, SectionHeader, StatusPill, SurfaceCard } from "../components/designSystem";

const plans = [
  {
    name: "Pro",
    accent: "brand",
    icon: Sparkles,
    description: "For serious individual traders who want deeper analytics, journal workflows, and exports.",
    features: ["Advanced analytics", "Unlimited trade reviews", "Journal and messaging", "CSV exports"],
  },
  {
    name: "Enterprise",
    accent: "warning",
    icon: Crown,
    description: "For desks, communities, and funded teams that need shared workflows and workspace visibility.",
    features: ["Team workspace", "Shared messaging", "Operational visibility", "Premium support"],
  },
];

export default function Upgrade() {
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-4 py-10 sm:px-6 lg:px-8">
      <SurfaceCard className="overflow-hidden border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.16),_transparent_22%),radial-gradient(circle_at_80%_20%,_rgba(251,191,36,0.18),_transparent_18%),linear-gradient(135deg,_#0f172a,_#111827_58%,_#1d4ed8)] text-white dark:border-gray-700">
        <SectionHeader
          eyebrow="Upgrade MekEdge"
          title="Build a trading journal worthy of capital and accountability"
          description="Move from simple record keeping into a disciplined performance system with better analytics, review tools, and collaboration depth. Payment is collected by bank transfer and activated after admin review."
          action={<StatusPill tone="warning">Premium access</StatusPill>}
        />
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <MetricCard label="Best for" value="Independent traders" helper="Pro sharpens decision review and execution feedback." className="bg-white/10 border-white/10" tone="text-white" />
          <MetricCard label="Best for" value="Trading desks" helper="Enterprise adds team structure and shared operating visibility." className="bg-white/10 border-white/10" tone="text-white" />
          <MetricCard label="Current plan" value={user?.plan ? user.plan.toUpperCase() : "FREE"} helper="Logged-in users can upgrade directly from the workspace." className="bg-white/10 border-white/10" tone="text-white" />
        </div>
      </SurfaceCard>

      <div className="grid gap-6 xl:grid-cols-2">
        {plans.map((plan) => {
          const Icon = plan.icon;
          return (
            <SurfaceCard key={plan.name}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="inline-flex rounded-2xl bg-slate-100 p-3 text-slate-900 dark:bg-gray-900 dark:text-white">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h2 className="mt-5 text-2xl font-semibold text-slate-950 dark:text-white">{plan.name}</h2>
                  <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">{plan.description}</p>
                </div>
                <StatusPill tone={plan.accent}>{plan.name}</StatusPill>
              </div>
              <div className="mt-6 space-y-3">
                {plan.features.map((feature) => (
                  <div key={feature} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-200">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-300" />
                    {feature}
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <Button type="button" variant="primary" onClick={() => navigate(token ? "/app/upgrade" : "/login") }>
                  {token ? `Open ${plan.name} payment flow` : "Login to upgrade"}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </SurfaceCard>
          );
        })}
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <SurfaceCard>
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-5 w-5 text-sky-600 dark:text-sky-300" />
            <div>
              <p className="text-sm font-semibold text-slate-950 dark:text-white">Serious review workflows</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Go beyond raw trade logging into accountable decision review.</p>
            </div>
          </div>
        </SurfaceCard>
        <SurfaceCard>
          <div className="flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-amber-600 dark:text-amber-300" />
            <div>
              <p className="text-sm font-semibold text-slate-950 dark:text-white">Analytics that change behavior</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">See drawdown, holding patterns, and instrument-level edge more clearly.</p>
            </div>
          </div>
        </SurfaceCard>
        <SurfaceCard>
          <div className="flex items-center gap-3">
            <Users className="h-5 w-5 text-fuchsia-600 dark:text-fuchsia-300" />
            <div>
              <p className="text-sm font-semibold text-slate-950 dark:text-white">Desk-ready collaboration</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Enterprise introduces shared operating context for teams and mentors.</p>
            </div>
          </div>
        </SurfaceCard>
      </div>
    </div>
  );
}